import React, { useState, useRef, useEffect } from 'react';
import { BetItem } from './BettingSlipDrawer';
import { EventSnapshot, Market, Outcome } from '../api/types';

interface MatchDetailPageProps {
  event: EventSnapshot;
  onBack: () => void;
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onLoginClick: () => void;
}

function formatOdds(val: string | number): string {
  const n = parseFloat(String(val));
  if (isNaN(n) || n <= 1) return '-';
  return n.toFixed(2).replace('.', ',');
}

function formatClock(ev: EventSnapshot): string {
  if (ev.status?.clock) return ev.status.clock.split(':')[0] + "'";
  return 'Live';
}

function formatMatchTime(ev: EventSnapshot): string {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return '';
  const d = new Date(starts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const timeStr = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `${timeStr} heute`;
  if (isTomorrow) return `${timeStr} morgen`;
  return `${timeStr} ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`;
}

// Market display configs
const MARKET_CONFIG: Record<string, { name: string; order: number }> = {
  '1':  { name: 'Tipp',              order: 1 },
  '18': { name: 'Über/Unter',        order: 2 },
  '10': { name: 'Doppelte Chance',   order: 3 },
  '29': { name: 'Beide Teams treffen', order: 4 },
  '16': { name: 'Halbzeitergebnis',  order: 5 },
};

function getOutcomeLabel(market: Market, outcome: Outcome, ev?: EventSnapshot): string {
  if (market.market_id === '1') {
    const map: Record<string, string> = { '1': '1', '2': 'X', '3': '2' };
    return map[outcome.outcome_id] ?? outcome.name;
  }
  if (market.market_id === '10') {
    const map: Record<string, string> = { '1': '1X', '2': 'X2', '3': '12' };
    return map[outcome.outcome_id] ?? outcome.name;
  }
  if (market.market_id === '29') {
    return outcome.name === 'yes' || outcome.outcome_id === '74' ? 'Ja' : 'Nein';
  }
  
  let res = outcome.name || '';
  if (market.specifiers) {
    if (market.specifiers.player) res = res.replace(/\{%player\}/g, 'Spieler');
    if (market.specifiers.total) res = res.replace(/\{total\}/g, market.specifiers.total);
    if (market.specifiers.goalnr) res = res.replace(/\{\!goalnr\}/g, market.specifiers.goalnr);
  }
  if (ev) {
    res = res.replace(/\{\$competitor1\}/g, ev.event.home.name);
    res = res.replace(/\{\$competitor2\}/g, ev.event.away.name);
  }
  res = res.replace(/draw/ig, 'X');
  return res;
}

function getSpecifierLabel(market: Market): string {
  if (!market.specifiers) return '';
  const total = market.specifiers['total'];
  if (total) return total;
  return '';
}

const JerseyIcon: React.FC<{ color?: string }> = ({ color = '#c0392b' }) => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 6 L6 14 L12 16 L12 40 H36 L36 16 L42 14 L34 6 Q29 10 24 10 Q19 10 14 6Z" fill={color} stroke="white" strokeWidth="1.5"/>
    <rect x="18" y="6" width="12" height="8" rx="2" fill="white" opacity="0.7"/>
  </svg>
);

const MatchDetailPage: React.FC<MatchDetailPageProps> = ({
  event: ev,
  onBack,
  bets,
  onAddBet,
  onRemoveBet,
  onLoginClick,
}) => {
  const [activeTab, setActiveTab] = useState<'alle' | 'haupt'>('haupt');
  const [heroVisible, setHeroVisible] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isLive = ev.scope === 'live';

  const simulatedMarkets: Market[] = [...(ev.markets ?? [])];

  const sortedMarkets = simulatedMarkets
    .filter((m) => m.status !== -1 && m.outcomes.length > 0)
    .sort((a, b) => {
      const orderA = MARKET_CONFIG[a.market_id]?.order ?? 99;
      const orderB = MARKET_CONFIG[b.market_id]?.order ?? 99;
      return orderA - orderB || a.market_id.localeCompare(b.market_id);
    });

  const hauptMarkets = sortedMarkets.filter((m) => MARKET_CONFIG[m.market_id]);
  const alleMarkets = sortedMarkets;
  const displayMarkets = activeTab === 'haupt' ? hauptMarkets : alleMarkets;

  // Group Über/Unter markets by specifier
  const overUnderMarkets = displayMarkets.filter((m) => m.market_id === '18');
  const otherMarkets = displayMarkets.filter((m) => m.market_id !== '18');

  const isBetSelected = (marketId: string, outcomeId: string, specifiers?: string) => {
    const betId = `detail-${ev.event_id}-${marketId}-${outcomeId}${specifiers ? `-${specifiers}` : ''}`;
    return bets.some((b) => b.id === betId);
  };

  const handleBetClick = (market: Market, outcome: Outcome) => {
    const specStr = market.specifiers
      ? Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
      : '';
    const betId = `detail-${ev.event_id}-${market.market_id}-${outcome.outcome_id}${specStr ? `-${specStr}` : ''}`;

    if (isBetSelected(market.market_id, outcome.outcome_id, specStr)) {
      onRemoveBet(betId);
      return;
    }

    const label = getOutcomeLabel(market, outcome, ev);
    let marketName = MARKET_CONFIG[market.market_id]?.name ?? market.name;
    if (marketName) {
      if (market.specifiers?.player) marketName = marketName.replace(/\{%player\}/g, 'Spieler');
      if (market.specifiers?.total) marketName = marketName.replace(/\{total\}/g, market.specifiers.total);
      if (market.specifiers?.goalnr) marketName = marketName.replace(/\{\!goalnr\}/g, market.specifiers.goalnr);
      if (ev) {
        marketName = marketName.replace(/\{\$competitor1\}/g, ev.event.home.name);
        marketName = marketName.replace(/\{\$competitor2\}/g, ev.event.away.name);
      }
    }
    const specLabel = getSpecifierLabel(market);
    const tippLabel = specLabel ? `${marketName} ${specLabel} – ${label}` : `${marketName} – ${label}`;

    onAddBet({
      id: betId,
      matchId: ev.event_id,
      matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
      betType: outcome.outcome_id === '1' ? 'home' : outcome.outcome_id === '3' ? 'away' : 'draw',
      tippLabel,
      odds: formatOdds(outcome.odds),
      eventId: ev.event_id,
      marketId: market.market_id,
      outcomeId: outcome.outcome_id,
      specifiers: specStr,
    });
  };

  const renderMarketCard = (market: Market) => {
    const activeOutcomes = market.outcomes.filter((o) => o.active);
    const specLabel = getSpecifierLabel(market);
    let marketName = MARKET_CONFIG[market.market_id]?.name ?? market.name;
    if (marketName) {
      if (market.specifiers?.player) marketName = marketName.replace(/\{%player\}/g, 'Spieler');
      if (market.specifiers?.total) marketName = marketName.replace(/\{total\}/g, market.specifiers.total);
      if (market.specifiers?.goalnr) marketName = marketName.replace(/\{\!goalnr\}/g, market.specifiers.goalnr);
      if (ev) {
        marketName = marketName.replace(/\{\$competitor1\}/g, ev.event.home.name);
        marketName = marketName.replace(/\{\$competitor2\}/g, ev.event.away.name);
      }
    }
    const displayName = specLabel ? `${marketName} ${specLabel}` : marketName;

    return (
      <div key={`${market.market_id}-${specLabel}`} className="bg-white rounded-xl overflow-hidden shadow-sm mb-2">
        <div className="px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-900">{displayName}</span>
        </div>
        <div className="flex px-3 py-3 gap-2">
          {activeOutcomes.map((outcome) => {
            const specStr = market.specifiers
              ? Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
              : '';
            const selected = isBetSelected(market.market_id, outcome.outcome_id, specStr);
            const oddsVal = formatOdds(outcome.odds);
            return (
              <button
                key={outcome.outcome_id}
                disabled={oddsVal === '-'}
                onClick={() => handleBetClick(market, outcome)}
                className={`flex-1 flex flex-col items-center justify-center rounded-lg px-2 py-2.5 transition min-w-0 ${
                  selected
                    ? 'bg-gray-900'
                    : oddsVal === '-'
                    ? 'bg-gray-50 opacity-40 cursor-not-allowed'
                    : 'bg-gray-100 active:bg-gray-200'
                }`}
              >
                <span className={`text-[11px] font-medium truncate w-full text-center ${selected ? 'text-gray-300' : 'text-gray-500'}`}>
                  {getOutcomeLabel(market, outcome, ev)}
                </span>
                <span className={`text-sm font-bold mt-0.5 ${selected ? 'text-white' : 'text-gray-800'}`}>
                  {oddsVal}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Group over/under markets into one card
  const renderOverUnderGroup = () => {
    if (overUnderMarkets.length === 0) return null;
    return (
      <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-2">
        <div className="px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-900">Über/Unter</span>
        </div>
        {overUnderMarkets.map((market) => {
          const activeOutcomes = market.outcomes.filter((o) => o.active);
          const specLabel = getSpecifierLabel(market);
          const specStr = market.specifiers
            ? Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
            : '';
          return (
            <div key={specStr} className="px-3 py-2 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 w-10 text-right">{specLabel}</span>
                <div className="flex flex-1 gap-2">
                  {activeOutcomes.map((outcome) => {
                    const selected = isBetSelected(market.market_id, outcome.outcome_id, specStr);
                    const oddsVal = formatOdds(outcome.odds);
                    return (
                      <button
                        key={outcome.outcome_id}
                        disabled={oddsVal === '-'}
                        onClick={() => handleBetClick(market, outcome)}
                        className={`flex-1 flex items-center justify-between rounded-lg px-3 py-2 transition ${
                          selected
                            ? 'bg-gray-900'
                            : oddsVal === '-'
                            ? 'bg-gray-50 opacity-40 cursor-not-allowed'
                            : 'bg-gray-100 active:bg-gray-200'
                        }`}
                      >
                        <span className={`text-xs truncate ${selected ? 'text-gray-300' : 'text-gray-500'}`}>
                          {outcome.name.toLowerCase().startsWith('over') || outcome.name.toLowerCase().startsWith('über') ? 'Über' : 'Unter'}
                        </span>
                        <span className={`text-sm font-bold ${selected ? 'text-white' : 'text-gray-800'}`}>
                          {oddsVal}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="h-full bg-gray-50 flex flex-col">

      {/* ── Sticky zone: header + tabs ─────────────────────────────────── */}
      <div className="sticky top-0 z-30 bg-white shadow-sm">

        {/* Header row — changes content based on hero visibility */}
        <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
          <button onClick={onBack} className="text-gray-800 p-1 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Normal: tournament name. Scrolled: compact match info */}
          {heroVisible ? (
            <span className="text-sm font-semibold text-gray-700 truncate mx-2 flex-1 text-center">
              {ev.event.tournament_name}, {ev.event.category_name}
            </span>
          ) : (
            <div className="flex items-center justify-center gap-2 flex-1 mx-2 min-w-0">
              <JerseyIcon color="#c0392b" />
              <span className="text-xs font-semibold text-gray-900 truncate max-w-[70px] text-right">{ev.event.home.name}</span>
              <div className="flex flex-col items-center flex-shrink-0 mx-1">
                {isLive ? (
                  <>
                    <span className="text-[10px] font-bold text-red-600 leading-none">Live</span>
                    <span className="text-base font-black text-gray-900 leading-tight">
                      {ev.status?.home_score ?? 0}:{ev.status?.away_score ?? 0}
                    </span>
                  </>
                ) : (
                  <span className="text-base font-black text-gray-900">
                    {new Date(ev.event.scheduled_at ?? ev.event.start_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <span className="text-xs font-semibold text-gray-900 truncate max-w-[70px] text-left">{ev.event.away.name}</span>
              <JerseyIcon color="#2c3e50" />
            </div>
          )}

          <button onClick={onLoginClick} className="text-sm font-semibold text-gray-700 hover:text-red-600 transition flex-shrink-0">
            Anmelden
          </button>
        </div>

        {/* Tabs — always sticky */}
        <div className="flex border-b border-gray-200">
          {[
            { id: 'haupt', label: 'Hauptwetten' },
            { id: 'alle', label: 'Alle Wetten' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'alle' | 'haupt')}
              className={`flex-1 py-3 text-sm font-semibold border-b-2 transition ${
                activeTab === tab.id
                  ? 'border-red-600 text-gray-900'
                  : 'border-transparent text-gray-500'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Match Hero (not sticky) ────────────────────────────────────── */}
      <div ref={heroRef} className="bg-white px-6 py-6 border-b border-gray-100">
        <div className="flex items-center justify-between gap-4">
          {/* Home Team */}
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <JerseyIcon color="#c0392b" />
            <span className="text-sm font-semibold text-gray-900 text-center leading-tight line-clamp-2">
              {ev.event.home.name}
            </span>
          </div>

          {/* Score / Time */}
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            {isLive ? (
              <>
                <span className="text-xs font-bold text-red-600">{formatClock(ev)}</span>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-black text-gray-900">{ev.status?.home_score ?? 0}</span>
                  <span className="text-xl font-bold text-gray-400">:</span>
                  <span className="text-3xl font-black text-gray-900">{ev.status?.away_score ?? 0}</span>
                </div>
                <span className="text-xs text-red-500 font-medium">● Live</span>
              </>
            ) : (
              <>
                <span className="text-lg font-black text-gray-900">
                  {new Date(ev.event.scheduled_at ?? ev.event.start_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="text-xs text-gray-500">
                  {(() => {
                    const d = new Date(ev.event.scheduled_at ?? ev.event.start_time);
                    const now = new Date();
                    const tomorrow = new Date(now);
                    tomorrow.setDate(now.getDate() + 1);
                    if (d.toDateString() === now.toDateString()) return 'heute';
                    if (d.toDateString() === tomorrow.toDateString()) return 'morgen';
                    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                  })()}
                </span>
                <span className="text-xs text-gray-400 mt-1">vs</span>
              </>
            )}
          </div>

          {/* Away Team */}
          <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
            <JerseyIcon color="#2c3e50" />
            <span className="text-sm font-semibold text-gray-900 text-center leading-tight line-clamp-2">
              {ev.event.away.name}
            </span>
          </div>
        </div>

        {/* Period scores */}
        {isLive && ev.status?.period_scores && ev.status.period_scores.length > 0 && (
          <div className="mt-3 flex justify-center gap-6">
            {ev.status.period_scores.map((p) => (
              <div key={p.number} className="text-center">
                <div className="text-xs text-gray-400">{p.number}. HZ</div>
                <div className="text-sm font-bold text-gray-700">{p.home_score}:{p.away_score}</div>
              </div>
            ))}
          </div>
        )}

        {/* Stats bar */}
        {isLive && ev.status?.statistics && (
          <div className="mt-3 flex justify-center gap-6 text-xs text-gray-500">
            {(ev.status.statistics.corners?.home !== undefined) && (
              <span>⛳ Ecken: {ev.status.statistics.corners!.home}:{ev.status.statistics.corners!.away}</span>
            )}
            {(ev.status.statistics.yellow_cards?.home !== undefined) && (
              <span>🟨 {ev.status.statistics.yellow_cards!.home}:{ev.status.statistics.yellow_cards!.away}</span>
            )}
            {(ev.status.statistics.red_cards?.home !== undefined) && (ev.status.statistics.red_cards!.home + ev.status.statistics.red_cards!.away > 0) && (
              <span>🟥 {ev.status.statistics.red_cards!.home}:{ev.status.statistics.red_cards!.away}</span>
            )}
          </div>
        )}
      </div>

      {/* Markets */}
      <div className="px-3 pt-3">
        {displayMarkets.length === 0 && (
          <p className="text-center text-gray-400 text-sm mt-10">Keine Wetten verfügbar.</p>
        )}

        {/* Render non-OU markets */}
        {otherMarkets.map((market) => renderMarketCard(market))}

        {/* Render Over/Under group */}
        {renderOverUnderGroup()}
      </div>
    </div>
  );
};

export default MatchDetailPage;
